/**
 * Existing-family detection for the Add Families wizard.
 *
 * Before writing anything, we check whether each roster row corresponds to a
 * child who is ALREADY in the school's system (added earlier, or through the
 * single-student flow). This drives two product rules:
 *
 *   1. Never delete or override an existing student. A row that matches an
 *      existing child is written non-destructively — we do NOT re-create or
 *      overwrite the student/contact. See `confirmFamilies`.
 *   2. If an existing family isn't on Wonderschool billing and the row has a
 *      tuition amount, add them to billing (create the recurring plan against
 *      the student that's already there) — without touching anything else.
 *
 * The match is by parent email + child full name (both normalized), which is
 * the same identity the wizard writes: the parent Contact is keyed by email,
 * and the child by name under that contact.
 *
 * Accepted limitation: check-then-write, so two directors confirming the same roster at
 * the same moment can still both create. Same race as every other CCMS write
 * path; a transaction or a uniqueness constraint in Postgres is the real fix
 * if simultaneous imports ever show up in support tickets.
 */
import { fetchActiveInvoicePlans } from '../../api/firebase/invoices';
import { fetchContactsByEmail } from '../../contacts/contactsAPI';
import { getStudentsForContact } from '../../students/studentsAPI';
import { childFullName, hasBillingAmount, normalizeName, RosterRow } from './types';

/**
 * What we found for one roster row, and therefore what confirm will do:
 *   - `new`                    — no match; create contact + student (+ plan).
 *   - `existing_no_change`     — child already in system, no tuition typed → skip.
 *   - `existing_add_billing`   — child already in system, not billed, tuition
 *                                typed → add a recurring plan, nothing else.
 *   - `existing_already_billed`— child already in system and already on an
 *                                active plan → skip (no double-billing).
 */
export type RowMatchStatus = 'new' | 'existing_no_change' | 'existing_add_billing' | 'existing_already_billed';

export interface RowMatch {
  status: RowMatchStatus;
  // Set when an existing child was matched — the docs confirm writes against
  // (billing-only) instead of creating new ones.
  existingContactId?: string;
  existingStudentId?: string;
  // The matched student doc as read from Firestore, carried so the billing
  // path can embed the real student as the plan's payer without another fetch.
  existingStudent?: Record<string, any>;
}

/**
 * Pure classifier: given what we found in the system for a row, decide the
 * outcome. Split out from the async fetching so it can be unit-tested.
 */
export function classifyMatch(
  found: { studentId?: string; contactId?: string; alreadyBilled: boolean },
  wantsBilling: boolean
): RowMatch {
  if (!found.studentId) return { status: 'new' };

  const base = { existingContactId: found.contactId, existingStudentId: found.studentId };
  if (!wantsBilling) return { status: 'existing_no_change', ...base };
  if (found.alreadyBilled) return { status: 'existing_already_billed', ...base };
  return { status: 'existing_add_billing', ...base };
}

// The subset of an existing student doc we read while matching (the full doc
// is carried through on `existingStudent`).
type StudentLite = { id: string; firstName?: string; lastName?: string; displayName?: string };

// `getStudentsForContact` lives in a .js module whose second param defaults to
// `null`, so TS infers its type as `null` and rejects a string org id. Wrap it
// once with the real signature instead of casting at the call site.
const fetchStudentsForContact = getStudentsForContact as (
  contactId: string,
  organizationId?: string
) => Promise<StudentLite[]>;

/**
 * Resolve each roster row against the school's existing data and return one
 * `RowMatch` per row (same order as `rows`). A row "wants billing" when it has
 * a tuition amount typed (the shipped wizard's per-row billing model).
 *
 * Fails CLOSED: any lookup error propagates and aborts the whole confirm. This
 * decides financial writes, and proceeding on empty evidence would classify
 * every existing family as `new` — re-creating the student and re-billing them.
 * A blocked import the director can retry beats a half-completed one.
 */
export async function matchRosterAgainstExisting(organizationId: string, rows: RosterRow[]): Promise<RowMatch[]> {
  // Fetch live recurring plans once; index the student ids they already
  // cover so "already on billing" is a Set lookup, not a per-row query.
  // No fallback: matching decides FINANCIAL writes, so a failed read must
  // abort the whole confirm (the caller surfaces "try again"), never proceed
  // on empty evidence — an empty set here would re-bill every existing family.
  const billedStudentIds = new Set<string>();
  const plans = await fetchActiveInvoicePlans(organizationId);
  for (const plan of (plans ?? []) as Array<{ students?: Array<{ id?: string }> }>) {
    for (const s of plan.students ?? []) {
      if (s?.id) billedStudentIds.add(s.id);
    }
  }

  // Cache existing students per contact id so siblings sharing a parent email
  // don't refetch. Cache contact-ids-by-email so we resolve each parent once.
  const studentsByContact = new Map<string, StudentLite[]>();
  const contactIdsByEmail = new Map<string, string[]>();

  // Per-row lookups also propagate errors (no fail-open): a row silently
  // classified "new" on a failed read becomes a duplicate student AND a
  // duplicate plan. The caller aborts and asks the user to retry instead.
  const matches: RowMatch[] = [];
  for (const row of rows) {
    {
      const email = row.parentEmail.trim().toLowerCase();
      if (!email) {
        matches.push({ status: 'new' });
        continue;
      }

      // ALL contacts on this email, not just one: historical duplicates exist,
      // and the child may hang off any of them. Missing that would re-create
      // the student and bill them a second time.
      let contactIds = contactIdsByEmail.get(email);
      if (!contactIds) {
        contactIds = (await fetchContactsByEmail(organizationId, email)).map((c) => c.id);
        contactIdsByEmail.set(email, contactIds);
      }

      if (contactIds.length === 0) {
        matches.push({ status: 'new' });
        continue;
      }

      const wantName = normalizeName(childFullName(row));
      let match: StudentLite | undefined;
      let matchedContactId: string | undefined;
      for (const contactId of contactIds) {
        let students = studentsByContact.get(contactId);
        if (!students) {
          students = (await fetchStudentsForContact(contactId, organizationId)) ?? [];
          studentsByContact.set(contactId, students);
        }
        match = students.find(
          (s) =>
            normalizeName(childFullName({ childFirstName: s.firstName ?? '', childLastName: s.lastName ?? '' })) ===
            wantName
        );
        if (match) {
          matchedContactId = contactId;
          break;
        }
      }

      const m = classifyMatch(
        {
          studentId: match?.id,
          contactId: matchedContactId ?? contactIds[0],
          alreadyBilled: match ? billedStudentIds.has(match.id) : false,
        },
        hasBillingAmount(row)
      );
      if (match) {
        m.existingStudent = match;
      }
      matches.push(m);
    }
  }

  return matches;
}

export interface MatchSummary {
  newCount: number;
  existingNoChange: number;
  existingAddBilling: number;
  existingAlreadyBilled: number;
}

/** Roll a list of matches up into the counts the confirm toast reports. */
export function summarizeMatches(matches: RowMatch[]): MatchSummary {
  const summary: MatchSummary = { newCount: 0, existingNoChange: 0, existingAddBilling: 0, existingAlreadyBilled: 0 };
  for (const m of matches) {
    if (m.status === 'new') summary.newCount += 1;
    else if (m.status === 'existing_no_change') summary.existingNoChange += 1;
    else if (m.status === 'existing_add_billing') summary.existingAddBilling += 1;
    else if (m.status === 'existing_already_billed') summary.existingAlreadyBilled += 1;
  }
  return summary;
}
